import React, { useState, useEffect, useRef } from 'react';
import { ArrowDown } from '@phosphor-icons/react';
import { motion } from '@/lib/motion';

const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || 'https://files.ihgbrands.com/ihg-ind';

// AI-generated hero video - Cinematic architectural metal finishes showcase
const getHeroVideo = () => `${MEDIA_URL}/videos/hero_video.mp4`;
// Fallback image for slower connections
const getHeroImage = () => `${MEDIA_URL}/generated/hero_main.webp`;

export const UnifiedHero = ({ heroData }) => {
  const [loaded, setLoaded] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef(null);
  const heroVideo = getHeroVideo();
  const heroImage = getHeroImage();

  const badge = heroData?.title || 'ENGINEERING PRECISION • ARTISTIC VISION';
  const subtitle = heroData?.subtitle || 'WHERE METAL BECOMES ART';
  const subtitleParts = subtitle.split('\n').length > 1 ? subtitle.split('\n') : subtitle.split(/(?<=\s)(?=\S+$)/);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleVideoLoad = () => {
    setVideoLoaded(true);
    if (videoRef.current) {
      videoRef.current.play().catch(console.log);
    }
  };

  const scrollToNext = () => {
    const nextSection = document.getElementById('dual-nature');
    nextSection?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative h-[70vh] md:h-screen min-h-[500px] md:min-h-[600px] max-h-[600px] md:max-h-[800px] bg-obsidian overflow-hidden" data-testid="unified-hero">
      {/* Background Video with Fallback Image */}
      <div className="absolute inset-0">
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ scale: loaded ? 1 : 1.1 }}
          transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          {/* Video Background */}
          <video
            ref={videoRef}
            src={heroVideo}
            className={`w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
            autoPlay
            muted
            loop
            playsInline
            onCanPlayThrough={handleVideoLoad}
            poster={heroImage}
          />
          {/* Fallback Image (shown while video loads) */}
          <img
            src={heroImage}
            alt="Architectural metal fabrication"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-0' : 'opacity-100'}`}
            loading="eager"
            fetchPriority="high"
            width={1920}
            height={800}
            decoding="async"
          />
        </motion.div>
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-obsidian/60" />
      </div>

      {/* Content - Center Aligned */}
      <div className="relative h-full max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="h-full flex flex-col items-center justify-center text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 20 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-6 md:mb-8"
          >
            <span className="inline-flex items-center gap-2 md:gap-3 text-bronze text-[9px] md:text-[10px] font-body font-semibold uppercase tracking-[0.2em] md:tracking-[0.3em]">
              <span className="w-6 md:w-10 h-px bg-bronze/60" />
              {badge}
              <span className="w-6 md:w-10 h-px bg-bronze/60" />
            </span>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 30 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white uppercase tracking-tight leading-[1.1]"
          >
            {subtitleParts[0]}
            {subtitleParts[1] && <><br /><span className="text-bronze">{subtitleParts[1]}</span></>}
          </motion.h1>
        </div>

        {/* Scroll Indicator */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: loaded ? 1 : 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          onClick={scrollToNext}
          className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/40 hover:text-white transition-colors"
          data-testid="hero-scroll-indicator"
        >
          <span className="text-[8px] md:text-[9px] font-body font-semibold uppercase tracking-[0.2em]">EXPLORE</span>
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <ArrowDown weight="bold" className="w-3.5 h-3.5 md:w-4 md:h-4" />
          </motion.div>
        </motion.button>
      </div>
    </section>
  );
};
